// Playing a set past a tune that will not load (Aug 2026).
//
// A playlist entry is a REFERENCE, and references go stale: a set saved this
// month can point at a tune since renamed or dropped from a corpus, or at a
// saved chart deleted from under it. Until now that ended the set — the load
// threw, the error was reported, and the run stopped on a tune nobody can hear.
//
// A set is an ordered list of tunes to play, so one unplayable entry should
// cost you that entry, not the rest of the gig. This walks forward to the
// first row that actually loads and reports what it passed over, so the jump
// is explained rather than mysterious.

import { entryIndex, type PlaylistDoc, type PlaylistEntry } from '@/storage/playlists';

/** The part of a tune row this needs: what it is called, and how to load it. */
export interface PlayableRow {
  playlistEntryId?: string;
  title: string;
  load: () => Promise<string>;
}

export interface SetLoadOutcome {
  /** The entry that actually loaded, or null if the set ran out. */
  played: PlaylistEntry | null;
  /** Titles passed over on the way, in set order. */
  skipped: string[];
  /** Why the last attempt failed — only meaningful when nothing loaded. */
  error: Error | null;
}

/**
 * Load the first entry from `from` onward that loads, skipping any that do not.
 *
 * Walks the set by POSITION, from `from` to the end, and never wraps, so it
 * always terminates rather than circling a list of broken references. Not by
 * stepping from id to id: ids can arrive duplicated (a shared set is another
 * account's document), and a step from the second of two rows sharing an id
 * landed back on the first, retrying the same dead row forever.
 *
 * Only forward: a set has an order, and hunting BACKWARDS for something
 * playable would take the player somewhere they have already been.
 */
export async function loadFirstPlayable(
  list: PlaylistDoc,
  from: PlaylistEntry,
  rows: readonly PlayableRow[],
): Promise<SetLoadOutcome> {
  const skipped: string[] = [];
  let error: Error | null = null;
  let start = list.entries.indexOf(from);
  if (start < 0) start = entryIndex(list, from.entryId);
  const walk = start < 0 ? [from] : list.entries.slice(start);
  const tried = new Set<PlayableRow>();

  for (const entry of walk) {
    // Rows pair with entries by id; where ids repeat, each row gets one turn.
    const row = rows.find((r) => r.playlistEntryId === entry.entryId && !tried.has(r));
    if (row) {
      tried.add(row);
      try {
        await row.load();
        return { played: entry, skipped, error: null };
      } catch (cause) {
        error = cause instanceof Error ? cause : new Error('Could not load that tune.');
        skipped.push(row.title);
      }
    } else {
      // No row built for this entry at all. Shouldn't happen — rows are built
      // FROM the entries — but a set that skips it is better than one that
      // stops on it, and the ref still knows what it was called.
      error = new Error('Could not load that tune.');
      skipped.push(entry.ref.title);
    }
  }

  return { played: null, skipped, error };
}

/**
 * How to explain a skip to someone who is looking at the chart, not the code.
 *
 * Names the tunes at one or two, counts them beyond that: a player wants to
 * know WHICH tune vanished out of a short set, and past that the number is the
 * only part they can act on.
 */
export function describeSkipped(skipped: readonly string[]): string | null {
  if (skipped.length === 0) return null;
  if (skipped.length === 1) return `Skipped ${skipped[0]} — it would not load.`;
  if (skipped.length === 2) return `Skipped ${skipped[0]} and ${skipped[1]} — they would not load.`;
  return `Skipped ${skipped.length} tunes that would not load.`;
}
